import React, { useEffect, useMemo, useRef, useState } from "react";
import { get, orderBy } from "lodash";
// Utils
import BetUtils from "@tvg/utils/betSelection";
// Store
import { useSelector } from "react-redux";
import { getBetType } from "@tvg/desktop-bet/src/store/selectors";
// Types
import {
  RaceProgram,
  RaceTypeCodeEnum,
  RaceWagerType
} from "@tvg/ts-types/Race";
import { getActiveButtons } from "@tvg/desktop-bet/src/utils/betUtils";
import { getIsLogged } from "@urp/store-selectors";
// Hooks
import { doesSilkLoads } from "@tvg/sh-utils/raceUtils";
import useGetRunnerFromBI, { RunnerBI } from "../../hooks/useGetRunnerFromBI";
import { getProgramPageModuleConfigs } from "../../store/selectors";
import {
  isWagerable as isWagerableUtil,
  filterHKRaces
} from "../../utils/raceUtils";
import { runnerSelectionEvent } from "../../utils/gtm/navigation";
import { Container } from "./styled-components";
import Runner from "../Runner";
import {
  RunnerModifierEnum,
  RunnerSortOrderEnum,
  RunnerSortByEnum
} from "../../types";
import Handicapping from "../Main/Handicapping";
import { getBestRunnersStats, getTopRunnersWithComments } from "./functions";

interface RunnerListProps {
  currentRace: RaceProgram;
  legPosition?: number;
  onClickBetButton?: (
    runner: RunnerBI,
    type: string,
    position?: number,
    module?: string
  ) => void;
  isScrollableRacecard?: boolean;
  scrollablePicksWagerType?: RaceWagerType;
  selections?: string[][];
  isBetSlipVisible?: boolean;
  runnerModifier?: RunnerModifierEnum;
  runnerSortOrder?: RunnerSortOrderEnum;
  runnerSortBy?: RunnerSortByEnum;
  numRunnersAllRaces?: Record<string, string | undefined>[];
  races?: Array<RaceProgram>;
}

const RunnerList = ({
  currentRace,
  legPosition = 0,
  scrollablePicksWagerType,
  onClickBetButton = () => ({}),
  selections = [],
  isBetSlipVisible = false,
  runnerModifier = RunnerModifierEnum.DEFAULT,
  runnerSortOrder = RunnerSortOrderEnum.ASC,
  runnerSortBy = RunnerSortByEnum.SADDLECLOTH,
  numRunnersAllRaces = [],
  isScrollableRacecard = false,
  races = []
}: RunnerListProps) => {
  const selectedBetType = useSelector(getBetType);
  const isLogged: boolean = useSelector(getIsLogged);
  const { handicappingModule } = useSelector(getProgramPageModuleConfigs);
  const isWagerable = isWagerableUtil(currentRace, isScrollableRacecard);
  const [runnersInfoOpen, setRunnersInfoOpen] = useState<Array<string>>([]);
  const [showSilksState, setShowSilksState] = useState(false);
  const silksError = useRef({});

  const runners = useMemo(() => {
    // Filtering only Hong Kong Races since when the race is Hong Kong
    // we need to remove the coupled runners starting on "S" ex: S1, S2, etc
    const filteredBI = filterHKRaces(currentRace);
    return useGetRunnerFromBI(filteredBI, { withComputedProps: true });
  }, [JSON.stringify(currentRace)]);

  const [runnersSorted, setRunnersSorted] = useState<Array<RunnerBI>>(
    runnerSortOrder !== RunnerSortOrderEnum.ASC ||
      runnerSortBy !== RunnerSortByEnum.SADDLECLOTH
      ? orderBy(runners, [runnerSortBy], [runnerSortOrder])
      : runners
  );

  useEffect(() => {
    if (currentRace.bettingInterests && currentRace.bettingInterests.length) {
      doesSilkLoads(currentRace.bettingInterests, false).then((result) => {
        setShowSilksState(result.some(Boolean));
      });
    }
  }, [JSON.stringify(currentRace.bettingInterests)]);

  useEffect(() => {
    setRunnersSorted(
      runnerSortOrder !== RunnerSortOrderEnum.ASC ||
        runnerSortBy !== RunnerSortByEnum.SADDLECLOTH
        ? orderBy(runners, [runnerSortBy], [runnerSortOrder])
        : runners
    );
  }, [runners]);

  useEffect(() => {
    setRunnersSorted(orderBy(runners, [runnerSortBy], [runnerSortOrder]));
  }, [runnerSortOrder, runnerSortBy]);

  const isDriverRace = currentRace?.type?.code === RaceTypeCodeEnum.HARNESS;
  const betTypeFromRace = isScrollableRacecard
    ? scrollablePicksWagerType
    : selectedBetType &&
      BetUtils.getBetTypeFromRace(currentRace, selectedBetType);

  const getJockeyhanges = (runnerNumber: string) => {
    const jockeyChanges = currentRace.changes?.horse?.jockey?.filter(
      (jockeyChange) => jockeyChange.runnerId === runnerNumber
    );

    return jockeyChanges || [];
  };

  const onInfoOpenToggleHandler = (runnerBiNumber: string) =>
    setRunnersInfoOpen((previous) =>
      previous.includes(runnerBiNumber)
        ? previous.filter((biNumber) => biNumber !== runnerBiNumber)
        : [...previous, runnerBiNumber]
    );

  const bestPerformanceRunners = useMemo(
    () => getBestRunnersStats(runners),
    [runners]
  );
  const hasRunnerSilk = runnersSorted.find((runner) => runner?.silkImage);

  const allRunnersHasSilksError =
    Object.keys(silksError.current).length === runnersSorted.length;

  const renderRunners = useMemo(
    () =>
      runnersSorted.map((runner, index, { length }) => runner && (
        <Runner
          isDriver={isDriverRace}
          key={`${index.toString()}-${runner.runnerName}`}
          runnerBI={runner}
          onClickBetButton={(type, _isActive, position) => {
            onClickBetButton(
              runner,
              type,
              isScrollableRacecard ? legPosition : position
            );
          }}
          wagerType={betTypeFromRace}
          hasBorderTop={index - 1 !== length && index > 0}
          activeButtons={getActiveButtons(
            runner.biNumber.toString(),
            isScrollableRacecard
              ? [get(selections, legPosition, [])]
              : selections
          )}
          isScrollableRacecard={isScrollableRacecard}
          isWagerable={isWagerable}
          currentRace={currentRace}
          changes={currentRace.changes?.horse}
          trackDataSource={currentRace.track?.trackDataSource}
          runnerModifier={runnerModifier}
          isLogged={isLogged}
          jockeyChanges={getJockeyhanges(runner.number)}
          isInfoOpen={runnersInfoOpen.includes(runner.number)}
          onInfoOpenToggle={(biNumber: string) => {
            onInfoOpenToggleHandler(biNumber);
            runnerSelectionEvent({
              runnerName: runner.runnerName,
              raceNumber: currentRace.raceNumber,
              trackName: currentRace.track?.trackName
            });
          }}
          bestPerformanceRunners={bestPerformanceRunners}
          hasRunnerSilk={hasRunnerSilk !== undefined}
          allRunnersHasSilksError={allRunnersHasSilksError}
          handleError={(runnerNumber) => {
            silksError.current = {
              ...silksError.current,
              [runnerNumber]: true
            };
          }}
          showSilks={showSilksState}
        />
      )),
    [
      JSON.stringify(runnersSorted),
      runnersInfoOpen,
      selectedBetType,
      JSON.stringify(selections),
      onClickBetButton,
      runnerModifier,
      isLogged,
      bestPerformanceRunners,
      hasRunnerSilk,
      allRunnersHasSilksError,
      silksError,
      showSilksState
    ]
  );

  const topRunnersWithComments = useMemo(
    () => getTopRunnersWithComments(runners),
    [runners]
  );

  const shouldRenderHandicappingModule =
    handicappingModule &&
    !isScrollableRacecard &&
    !!(
      topRunnersWithComments.length ||
      (currentRace.talentPicks && currentRace.talentPicks.length)
    );

  return (
    <Container>
      {renderRunners}
      {shouldRenderHandicappingModule && (
        <Handicapping
          isScrollableRacecard={isScrollableRacecard}
          legPosition={legPosition}
          race={currentRace}
          numRunnersAllRaces={numRunnersAllRaces}
          runners={topRunnersWithComments}
          wagerType={betTypeFromRace}
          selections={selections}
          onClickBetButton={onClickBetButton}
          isWagerable={isWagerable}
          isBetSlipVisible={isBetSlipVisible}
          isLogged={isLogged}
          races={races}
        />
      )}
    </Container>
  );
};

export default RunnerList;
